import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDoc, useOrderedCol, useProgress } from '../lib/hooks';
import { useAuthCtx } from '../lib/auth-context';
import type { ChapterDoc, CourseDoc, ResourceDoc, SectionDoc } from '../lib/data-model';
import { Loading, ErrorBlock, EmptyState, CheckButton } from '../components/ui';
import { AdSlot } from '../components/AdSlot';
import { kindBadge, resMeta } from './public-helpers/misc';
import { logActivity } from '../lib/study';

export default function ChapterPage() {
  const { chapterId = '' } = useParams();
  const auth = useAuthCtx();
  const { done, toggleDone, toggleDoneMany } = useProgress(auth.isSignedIn);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const chapter = useDoc<ChapterDoc>('chapters', chapterId);
  const course = useDoc<CourseDoc>('courses', chapter.data?.courseId ?? '', !!chapter.data);
  // Whole-collection reads with client-side filters: composite indexes are not deployed.
  const allSections = useOrderedCol<SectionDoc>('sections');
  const allRes = useOrderedCol<ResourceDoc>('resources');
  const sections = {
    data: allSections.data.filter(s => s.chapterId === chapterId),
    loading: allSections.loading,
    error: allSections.error
  };
  const courseRes = {
    data: allRes.data.filter(r => r.courseId === (chapter.data?.courseId ?? '\u0000')),
    loading: allRes.loading,
    error: allRes.error
  };

  const visibleSections = useMemo(
    () => sections.data.filter(s => !s.hidden).sort((a, b) => a.order - b.order),
    [sections.data]
  );

  const bySection = useMemo(() => {
    const m = new Map<string, Array<ResourceDoc & { id: string }>>();
    for (const r of courseRes.data) {
      if (r.hidden) continue;
      const arr = m.get(r.sectionId) ?? [];
      arr.push(r);
      m.set(r.sectionId, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.order - b.order);
    return m;
  }, [courseRes.data]);

  const chapterResIds = useMemo(() => {
    const secIds = new Set(visibleSections.map(s => s.id));
    return courseRes.data.filter(r => !r.hidden && secIds.has(r.sectionId)).map(r => r.id);
  }, [courseRes.data, visibleSections]);

  const titleById = useMemo(() => new Map(courseRes.data.map(r => [r.id, r.title] as const)), [courseRes.data]);
  const logResourceDone = (id: string) => {
    if (!done.has(id)) logActivity('#2FBF6D', `Completed: ${titleById.get(id) ?? 'resource'}`);
  };
  const logSectionDone = (ids: string[], complete: boolean, secTitle: string) => {
    if (complete && ids.length && !ids.every(id => done.has(id))) logActivity('#F0A800', `Completed section: ${secTitle}`);
  };

  const chapDone = chapterResIds.filter(id => done.has(id)).length;
  const chapTotal = chapterResIds.length;
  const chapPct = chapTotal ? Math.round((chapDone / chapTotal) * 100) : 0;

  if (chapter.error) return <ErrorBlock message={chapter.error} />;
  if (chapter.loading) return <Loading label="Loading chapter…" />;
  if (!chapter.data) {
    return (
      <EmptyState
        glyph="✦"
        title="Chapter not found"
        body="This chapter may have been removed or unpublished."
        action={<Link className="btn btn-outline" to="/">← All chapters</Link>}
      />
    );
  }

  const toggleOpen = (id: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="page-anim">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden>›</span>
        <Link to={`/c/${chapter.data.courseId}`}>{course.data?.name ?? 'Course'}</Link>
        <span aria-hidden>›</span>
        <span>{chapter.data.title}</span>
      </nav>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 30 }}>
        <div style={{ flex: '1 1 420px' }}>
          <div className="mono muted" style={{ fontSize: 12, letterSpacing: '.08em', marginBottom: 10 }}>
            CHAPTER {chapter.data.num}
          </div>
          <h1 className="page-title" style={{ marginBottom: 12 }}>{chapter.data.title}</h1>
          <p className="lede">{chapter.data.blurb}</p>
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 18, padding: '20px 22px', minWidth: 210 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                position: 'relative', width: 64, height: 64, borderRadius: '50%',
                background: `conic-gradient(var(--brand) ${chapPct * 3.6}deg, var(--surface2) 0)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              role="img"
              aria-label={`Chapter progress ${chapPct}%`}
            >
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 500 }}>
                {chapPct}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 3 }}>Chapter progress</div>
              <div style={{ fontSize: 13, color: 'var(--ink3)' }}>{chapDone} of {chapTotal} resources</div>
            </div>
          </div>
        </div>
      </div>

      <AdSlot variant="leaderboard" />

      {sections.error ? (
        <ErrorBlock message={sections.error} />
      ) : sections.loading ? (
        <Loading label="Loading sections…" />
      ) : visibleSections.length === 0 ? (
        <EmptyState glyph="✦" title="Nothing here yet" body="This chapter has no published sections yet." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {visibleSections.map((sec, si) => (
            <SectionRow
              key={sec.id}
              section={sec}
              num={si + 1}
              items={bySection.get(sec.id) ?? []}
              done={done}
              isOpen={open.has(sec.id)}
              onExpand={() => toggleOpen(sec.id)}
              onToggleSection={(ids, complete) => {
                toggleDoneMany(ids, complete);
                logSectionDone(ids, complete, sec.title);
              }}
              onToggleResource={(id) => {
                toggleDone(id);
                logResourceDone(id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionRow({
  section,
  num,
  items,
  done,
  isOpen,
  onExpand,
  onToggleSection,
  onToggleResource
}: {
  section: SectionDoc & { id: string };
  num: number;
  items: Array<ResourceDoc & { id: string }>;
  done: Set<string>;
  isOpen: boolean;
  onExpand: () => void;
  onToggleSection: (ids: string[], complete: boolean) => void;
  onToggleResource: (id: string) => void;
}) {
  const ids = items.map(r => r.id);
  const doneN = ids.filter(id => done.has(id)).length;
  const allDone = ids.length > 0 && doneN === ids.length;
  const pct = ids.length ? Math.round((doneN / ids.length) * 100) : 0;
  const bar = pct === 100 ? 'var(--brand)' : pct > 0 ? '#FFC53D' : 'var(--line)';

  return (
    <div className={`sec-row${isOpen ? ' open' : ''}`}>
      <div className="sec-head">
        <CheckButton
          on={allDone}
          label={section.title}
          onToggle={() => onToggleSection(ids, !allDone)}
        />
        <button className="sec-title" onClick={onExpand} aria-expanded={isOpen}>
          <span className="k">SECTION {num}</span>
          <span className="t">{section.title}</span>
          <span className="s">{section.summary}</span>
        </button>
        <div style={{ flex: '0 1 190px', minWidth: 150 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--ink3)', marginBottom: 6 }}>
            <span>{doneN}/{ids.length} done</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 7, borderRadius: 99, background: 'rgba(23,26,23,.08)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 99, background: bar, width: `${pct}%`, transition: 'width .5s ease' }} />
          </div>
        </div>
        <button
          className="icon-btn"
          onClick={onExpand}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} section ${section.title}`}
          style={{ flex: 'none' }}
        >
          <span aria-hidden>{isOpen ? '▴' : '▾'}</span>
        </button>
      </div>

      {isOpen && (
        <div className="sec-items">
          {items.length === 0 && <p className="muted" style={{ margin: '6px 4px' }}>No resources in this section yet.</p>}
          {items.map(r => {
            const badge = kindBadge(r.kind);
            return (
              <div className="res-row" key={r.id}>
                <span className="res-badge" style={{ background: badge.bg, color: badge.fg }} aria-hidden>
                  {badge.glyph}
                </span>
                <Link className="res-meta" to={`/res/${r.id}`}>
                  <span className="t">{r.title}</span>
                  <span className="m">{resMeta(r.meta, r.kind)}</span>
                </Link>
                <CheckButton small on={done.has(r.id)} label={r.title} onToggle={() => onToggleResource(r.id)} />
                <Link className="btn btn-outline btn-sm" to={`/res/${r.id}`} style={{ flex: 'none' }}>
                  Open →
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
